"use client";

import type React from "react";
import { useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useAnimationFrame,
  useMotionTemplate,
  useMotionValue,
  useTransform,
} from "motion/react";
import Image from "next/image";
import { cn } from "@frontend/shared/lib/utils";

export function Button({
  borderRadius = "1.75rem",
  children,
  as: Component = "button",
  containerClassName,
  borderClassName,
  duration,
  className,
  icon = false,
  iconInline = false,
  hoverAnimation = false,
  variant = "dark",
  ...otherProps
}: {
  borderRadius?: string;
  children: React.ReactNode;
  // biome-ignore lint/suspicious/noExplicitAny: polymorphic component pattern
  as?: any;
  containerClassName?: string;
  borderClassName?: string;
  duration?: number;
  className?: string;
  icon?: boolean;
  /** When true, the icon renders inline next to the text (centered as a group)
   * instead of being absolutely positioned against the button's left edge. */
  iconInline?: boolean;
  hoverAnimation?: boolean;
  /** "light" = white bg, black text; "dark" = black bg, white text */
  variant?: "light" | "dark";
  [key: string]: unknown;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const bg = variant === "light" ? "#ffffff" : "#000000";
  const color = variant === "light" ? "#000000" : "#ffffff";

  return (
    <Component
      className={cn(
        "relative h-16 w-40 overflow-hidden bg-transparent p-[3px] text-xl",
        containerClassName,
      )}
      style={{
        borderRadius: borderRadius,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...otherProps}
    >
      <div
        className="absolute inset-0"
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        <MovingBorder duration={duration} rx="30%" ry="30%">
          <div
            className={cn(
              "h-24 w-48 bg-[radial-gradient(#00C2FF_40%,transparent_60%)] opacity-[0.8]",
              borderClassName,
            )}
          />
        </MovingBorder>
      </div>

      <motion.div
        className={cn(
          "relative flex h-full w-full items-center justify-center gap-2 antialiased backdrop-blur-xl",
          className,
        )}
        style={{
          borderRadius: `calc(${borderRadius} * 0.96)`,
          backgroundColor: bg,
          color: color,
          paddingLeft: "1.5rem",
          paddingRight: "1.5rem",
        }}
        animate={
          hoverAnimation
            ? {
                backgroundColor: bg,
                color: color,
                paddingLeft:
                  icon && !iconInline && isHovered ? "3.5rem" : "1.5rem",
                paddingRight: "1.5rem",
              }
            : undefined
        }
        transition={
          hoverAnimation ? { duration: 0.3, ease: "easeInOut" } : undefined
        }
      >
        {icon && iconInline && (
          <motion.div
            className="relative z-10 flex items-center overflow-hidden"
            animate={{
              width: isHovered ? 32 : 0,
              opacity: isHovered ? 1 : 0,
            }}
            initial={{ width: 0, opacity: 0 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 25,
            }}
          >
            <Image
              src="/icons/SuperscriptCurvesIcon.svg"
              alt=""
              width={32}
              height={32}
              className="flex-shrink-0"
            />
          </motion.div>
        )}
        <AnimatePresence>
          {icon && !iconInline && isHovered && (
            <motion.div
              className="absolute left-4 top-1/2"
              initial={{ opacity: 0, x: -10, y: "-50%", scale: 0.5 }}
              animate={{ opacity: 1, x: 0, y: "-50%", scale: 1 }}
              exit={{ opacity: 0, x: -10, y: "-50%", scale: 0.5 }}
              transition={{
                type: "spring",
                stiffness: 400,
                damping: 15,
                duration: 0.4,
              }}
            >
              <Image
                src="/icons/SuperscriptCurvesIcon.svg"
                alt=""
                width={32}
                height={32}
              />
            </motion.div>
          )}
        </AnimatePresence>
        <span className="relative z-10">{children}</span>
      </motion.div>
    </Component>
  );
}

export const MovingBorder = ({
  children,
  duration = 3000,
  rx,
  ry,
  ...otherProps
}: {
  children: React.ReactNode;
  duration?: number;
  rx?: string;
  ry?: string;
  [key: string]: unknown;
}) => {
  const pathRef = useRef<SVGRectElement>(null);
  const progress = useMotionValue<number>(0);

  useAnimationFrame((time) => {
    try {
      const length = pathRef.current?.getTotalLength();
      if (length) {
        const pxPerMillisecond = length / duration;
        progress.set((time * pxPerMillisecond) % length);
      }
    } catch {
      // getTotalLength throws InvalidStateError when the SVG element is
      // still in the DOM but no longer rendered (e.g. mid-unmount).
    }
  });

  const x = useTransform(progress, (val) => {
    try {
      const length = pathRef.current?.getTotalLength();
      if (!length) return 0;
      return pathRef.current?.getPointAtLength(val).x ?? 0;
    } catch {
      return 0;
    }
  });
  const y = useTransform(progress, (val) => {
    try {
      const length = pathRef.current?.getTotalLength();
      if (!length) return 0;
      return pathRef.current?.getPointAtLength(val).y ?? 0;
    } catch {
      return 0;
    }
  });

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        className="absolute h-full w-full"
        width="100%"
        height="100%"
        aria-hidden="true"
        {...otherProps}
      >
        <rect
          fill="none"
          width="100%"
          height="100%"
          rx={rx}
          ry={ry}
          ref={pathRef}
        />
      </svg>
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          display: "inline-block",
          transform,
        }}
      >
        {children}
      </motion.div>
    </>
  );
};
